import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useMessages, useInvalidate } from "@/lib/queries";
import { usePropertyFilter } from "@/context/PropertyFilter";
import OnboardingBanner from "@/components/OnboardingBanner";
import OtaRequestCard from "@/components/OtaRequestCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PaperPlaneTilt, ChatCircle, ArrowLeft, Sparkle, X, MagnifyingGlass,
  Trash, EnvelopeOpen, WhatsappLogo, CheckCircle, Warning, Note, PencilSimple, FloppyDisk,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const PLATFORM_META = {
  airbnb:    { label: "Airbnb",       bg: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400" },
  booking:   { label: "Booking.com",  bg: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" },
  vrbo:      { label: "VRBO",         bg: "bg-teal-100 text-teal-600" },
  mmt:       { label: "MakeMyTrip",   bg: "bg-red-100 text-red-600" },
  goibibo:   { label: "Goibibo",      bg: "bg-cyan-100 text-cyan-600" },
  agoda:     { label: "Agoda",        bg: "bg-orange-100 text-orange-600" },
  whatsapp:  { label: "WhatsApp",     bg: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" },
  expedia:   { label: "Expedia",      bg: "bg-yellow-100 text-yellow-700" },
};
// Aiosell sends its own channel names; map them onto the labels above.
PLATFORM_META["booking.com"] = PLATFORM_META.booking;
PLATFORM_META.gommt = PLATFORM_META.mmt;

const STATUS_META = {
  new:            { label: "New",            cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  awaiting_reply: { label: "Awaiting Reply", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  open:           { label: "Open",           cls: "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400" },
  resolved:       { label: "Resolved",       cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
};


export default function Inbox() {
  const { selectedPropertyId } = usePropertyFilter();
  const invalidate              = useInvalidate();
  const { data: msgData = [], isLoading: msgLoading } = useMessages();
  const [conversations, setConversations] = useState([]);
  const [properties, setProperties]       = useState([]);
  const [templates, setTemplates]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState(null);
  const [replyText, setReplyText]         = useState("");
  const [smartReplies, setSmartReplies]   = useState(null);
  const [detectedLang, setDetectedLang]   = useState({ code: "en", name: "Hindi" });
  const [loadingAI, setLoadingAI]         = useState(false);
  const [sendViaWhatsApp, setSendViaWhatsApp] = useState(false);
  const [search, setSearch]               = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [showNotes, setShowNotes]         = useState(false);
  const [noteText, setNoteText]           = useState("");
  const [tmplDialogOpen, setTmplDialogOpen] = useState(false);
  const [editTmpl, setEditTmpl]           = useState(null);
  const [tmplForm, setTmplForm]           = useState({ label: "", text: "" });
  // Scroll only the thread box; scrollIntoView also scrolled the whole page and jumped it.
  const threadRef = useRef(null);
  const sendingRef = useRef(false);
  const scrollThread = (ms) => setTimeout(() => {
    const el = threadRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, ms);

  // Sync React Query data into local state (keeps existing selected-conversation logic intact)
  useEffect(() => {
    setConversations(msgData);
    if (selected) {
      const updated = msgData.find(c => c.id === selected.id);
      if (updated) setSelected(updated);
    }
  }, [msgData]);  // eslint-disable-line react-hooks/exhaustive-deps

  // WhatsApp conversations default to sending via WhatsApp
  useEffect(() => {
    const isWa = (selected?.channel === "whatsapp" || selected?.platform === "whatsapp") && !!selected?.guest_phone;
    setSendViaWhatsApp(isWa);
  }, [selected?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load properties + templates once on mount (not message-list dependent)
  useEffect(() => {
    Promise.all([
      api.get("/properties"),
      api.get("/inbox/templates"),
    ]).then(([p, t]) => {
      setProperties(p.data);
      setTemplates(t.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const isLoading = msgLoading || loading;

  const reload = () => {
    invalidate.messages();
  };

  const propMap = {};
  properties.forEach(p => { propMap[p.id] = p; });

  // One button per channel the host actually has conversations on, so a new OTA shows up by itself.
  // Aiosell spells channels its own way ("booking.com", "gommt"); filter on one key per OTA.
  const platKey = (c) => ({ "booking.com": "booking", gommt: "mmt" })[c.platform] || c.platform || "hostfolio";
  // Airbnb and Booking.com always show (the OTAs we relay); others appear once a conversation exists.
  const ALL_PLATFORMS = [...new Set(["all", "airbnb", "booking", ...conversations.map(platKey)])].filter(p => p !== "hostfolio");

  const filtered = conversations.filter(c => {
    if (selectedPropertyId && c.property_id !== selectedPropertyId) return false;
    if (search && !c.guest_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (platformFilter !== "all" && platKey(c) !== platformFilter) return false;
    return true;
  });

  const unreadCount = conversations.filter(c => !c.is_read).length;

  const handleSendReply = async () => {
    // Enter fired three sends in 2s on 19 Oct; one in flight at a time.
    if (!replyText.trim() || !selected || sendingRef.current) return;
    sendingRef.current = true;
    try {
      const { data } = await api.post(`/messages/${selected.id}/reply`, {
        sender: "owner",
        text: replyText.trim(),
        send_via_whatsapp: sendViaWhatsApp && !!selected.guest_phone,
      });
      setSelected(data);
      setReplyText("");
      reload();
      if (sendViaWhatsApp && selected.guest_phone) {
        if (data?.wa_sent === false) {
          toast.error("Saved to thread, but WhatsApp delivery failed — guest may be outside the 24h reply window");
        } else {
          toast.success("Sent via WhatsApp");
        }
      }
      if (data?.ota_sent === false) {
        toast.error(`Not delivered to ${PLATFORM_META[selected.platform]?.label || "the booking site"}. The guest has not received it, please reply there for now.`);
      }
      scrollThread(100);
    } catch { toast.error("Failed to send"); }
    finally { sendingRef.current = false; }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/messages/${id}`);
      if (selected?.id === id) setSelected(null);
      setConversations(prev => prev.filter(c => c.id !== id));
      invalidate.messages();
      toast.success("Conversation deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleMarkUnread = async (id) => {
    try {
      await api.put(`/messages/${id}/unread`);
      reload();
    } catch { toast.error("Failed"); }
  };

  const handleSetStatus = async (id, status) => {
    try {
      await api.put(`/messages/${id}/status`, { status });
      reload();
      if (status === "resolved") toast.success("Marked as resolved");
    } catch { toast.error("Failed"); }
  };

  const handleToggleUrgent = async () => {
    if (!selected) return;
    try {
      const { data } = await api.put(`/messages/${selected.id}/urgent`, { is_urgent: !selected.is_urgent });
      setSelected(prev => ({ ...prev, is_urgent: data.is_urgent }));
      reload();
    } catch { toast.error("Failed"); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selected) return;
    try {
      await api.post(`/messages/${selected.id}/notes`, { text: noteText.trim() });
      setNoteText("");
      reload();
    } catch { toast.error("Failed to add note"); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selected) return;
    try {
      await api.delete(`/messages/${selected.id}/notes/${noteId}`);
      reload();
    } catch { toast.error("Failed to delete note"); }
  };

  const getSmartReplies = async () => {
    if (!selected) return;
    setLoadingAI(true);
    setSmartReplies(null);
    try {
      const { data } = await api.post(`/messages/${selected.id}/smart-reply`);
      setSmartReplies(data.suggestions || []);
      const lang = { code: data.lang_detected || "en", name: data.lang_name || "Hindi" };
      setDetectedLang(lang);
      if (data.lang_detected && data.lang_detected !== "en") {
        toast.info(`${lang.name} detected — local reply included`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Could not generate AI replies"); }
    finally { setLoadingAI(false); }
  };

  const gridRef = useRef(null);
  const selectConvo = async (c) => {
    setSelected(c);
    // Phone: the thread replaces the list, so bring its top (and the reply box) into view once.
    if (window.innerWidth < 768) setTimeout(() => gridRef.current?.scrollIntoView({ block: "start" }), 50);
    setShowNotes(false);
    setSendViaWhatsApp(c.channel === "whatsapp" && !!c.guest_phone);
    if (!c.is_read) {
      await api.put(`/messages/${c.id}/read`);
      reload();
    }
    scrollThread(150);
  };

  // ── Template CRUD ──
  const openNewTmpl = () => { setEditTmpl(null); setTmplForm({ label: "", text: "" }); setTmplDialogOpen(true); };
  const openEditTmpl = (t) => { setEditTmpl(t); setTmplForm({ label: t.label, text: t.text }); setTmplDialogOpen(true); };
  const handleSaveTmpl = async () => {
    if (!tmplForm.label.trim() || !tmplForm.text.trim()) { toast.error("Label and text are required"); return; }
    try {
      if (editTmpl) {
        await api.put(`/inbox/templates/${editTmpl.id}`, tmplForm);
        toast.success("Template updated");
      } else {
        await api.post("/inbox/templates", tmplForm);
        toast.success("Template saved");
      }
      setTmplDialogOpen(false);
      const { data } = await api.get("/inbox/templates");
      setTemplates(data);
    } catch { toast.error("Failed to save template"); }
  };
  const handleDeleteTmpl = async (id) => {
    try {
      await api.delete(`/inbox/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  if (isLoading) return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 bg-slate-200 rounded" />
      <div className="h-96 bg-slate-100 rounded-2xl" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="inbox-page">
      <OnboardingBanner pageKey="inbox" />
      {/* Header */}
      <div className={`items-center justify-between ${selected ? "hidden md:flex" : "flex"}`}>
        <div>
          <h1 className="font-manrope text-2xl md:text-3xl font-semibold text-slate-900 dark:text-amber-400 tracking-tight flex items-center gap-2">
            Inbox
            {unreadCount > 0 && <Badge className="bg-amber-400 text-white text-xs">{unreadCount}</Badge>}
          </h1>
          <p className="text-slate-500 dark:text-slate-200 text-sm mt-1">{filtered.length !== conversations.length ? `${filtered.length} of ` : ""}{conversations.length} conversation{conversations.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* Search + platform filter */}
      <div className={`gap-2 flex-wrap ${selected ? "hidden md:flex" : "flex"}`}>
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guests…"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {ALL_PLATFORMS.map(p => (
            <button key={p} onClick={() => setPlatformFilter(p)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                platformFilter === p ? "bg-amber-400 text-white" : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
              }`}>
              {p === "all" ? "All" : (PLATFORM_META[p]?.label || p)}
            </button>
          ))}
        </div>
      </div>

      <div ref={gridRef} className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100dvh-80px)] md:h-[calc(100dvh-230px)] min-h-[440px] scroll-mt-20">
        {/* Conversation List */}
        <div className={`space-y-2 overflow-y-auto min-h-0 pr-1 ${selected ? "hidden md:block" : ""}`}>
          {filtered.map(c => {
            const prop = propMap[c.property_id];
            const lastMsg = c.messages?.[c.messages.length - 1];
            const statusMeta = STATUS_META[c.status] || STATUS_META.open;
            return (
              <div key={c.id} onClick={() => selectConvo(c)}
                className={`group w-full text-left p-3 rounded-xl transition-colors cursor-pointer ${
                  selected?.id === c.id
                    ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"
                    : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 shadow-sm"
                } ${c.is_urgent ? "border-l-2 border-l-red-400" : ""}`}>
                <div className="flex items-start justify-between gap-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {c.is_urgent && <Warning size={12} weight="fill" className="text-red-500 shrink-0" />}
                      <p className={`text-sm font-semibold truncate ${!c.is_read ? "text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-200"}`}>
                        {c.guest_name}
                      </p>
                      {!c.is_read && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {c.channel === "whatsapp" && <WhatsappLogo size={11} weight="fill" className="text-green-500 shrink-0" />}
                      {c.platform && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${PLATFORM_META[c.platform]?.bg || "bg-slate-100 text-slate-500"}`}>
                          {PLATFORM_META[c.platform]?.label || c.platform}
                        </span>
                      )}
                      {c.status && c.status !== "open" && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${statusMeta.cls}`}>
                          {statusMeta.label}
                        </span>
                      )}
                      <p className="text-xs text-slate-400 truncate">{prop?.name || ""}</p>
                    </div>
                    {/* Booking context */}
                    {(c.check_in || c.check_out) && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {c.check_in} → {c.check_out}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {c.is_read && (
                      <button onClick={e => { e.stopPropagation(); handleMarkUnread(c.id); }} title="Mark as unread"
                        className="p-1 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50">
                        <EnvelopeOpen size={13} />
                      </button>
                    )}
                    <button onClick={e => handleDelete(c.id, e)} title="Delete"
                      className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50">
                      <Trash size={13} />
                    </button>
                  </div>
                </div>
                {lastMsg && (
                  <p className="text-xs text-slate-400 truncate mt-1">{lastMsg.text}</p>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <ChatCircle size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">{conversations.length === 0 ? "No conversations yet" : "No matches"}</p>
              {conversations.length === 0 && (
                <p className="text-slate-300 text-xs mt-1">WhatsApp messages and iCal bookings appear here automatically</p>
              )}
            </div>
          )}
        </div>

        {/* Message Thread */}
        {selected ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col shadow-sm min-h-0 overflow-hidden">
            {/* Thread header */}
            <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center gap-3">
              <button onClick={() => setSelected(null)} className="md:hidden text-slate-400 hover:text-slate-700">
                <ArrowLeft size={20} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {selected.channel === "whatsapp" && <WhatsappLogo size={14} weight="fill" className="text-green-500 shrink-0" />}
                  <p className="text-sm text-slate-800 dark:text-white font-semibold truncate">{selected.guest_name}</p>
                  {selected.platform && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${PLATFORM_META[selected.platform]?.bg || "bg-slate-100 text-slate-500"}`}>
                      {PLATFORM_META[selected.platform]?.label || selected.platform}
                    </span>
                  )}
                  {selected.status && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${(STATUS_META[selected.status] || STATUS_META.open).cls}`}>
                      {(STATUS_META[selected.status] || STATUS_META.open).label}
                    </span>
                  )}
                  {selected.is_urgent && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-red-100 text-red-600">Urgent</span>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  {propMap[selected.property_id]?.name || ""}
                  {selected.check_in && <span className="ml-2">{selected.check_in} → {selected.check_out}</span>}
                  {selected.guest_phone && <span className="ml-2 text-slate-300">+91 {selected.guest_phone}</span>}
                </p>
              </div>
              {/* Action buttons */}
              <div className="flex gap-1 shrink-0">
                <button onClick={handleToggleUrgent} title={selected.is_urgent ? "Remove urgent" : "Mark urgent"}
                  className={`p-1.5 rounded-lg transition-colors ${selected.is_urgent ? "text-red-500 bg-red-50" : "text-slate-400 hover:text-red-500 hover:bg-red-50"}`}>
                  <Warning size={16} weight={selected.is_urgent ? "fill" : "regular"} />
                </button>
                <button onClick={() => setShowNotes(v => !v)} title="Internal notes"
                  className={`p-1.5 rounded-lg transition-colors ${showNotes ? "text-amber-500 bg-amber-50" : "text-slate-400 hover:text-amber-500 hover:bg-amber-50"}`}>
                  <Note size={16} />
                </button>
                {selected.status !== "resolved" ? (
                  <button onClick={() => handleSetStatus(selected.id, "resolved")} title="Mark as resolved"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 transition-colors">
                    <CheckCircle size={16} />
                  </button>
                ) : (
                  <button onClick={() => handleSetStatus(selected.id, "open")} title="Reopen"
                    className="p-1.5 rounded-lg text-emerald-500 bg-emerald-50 hover:bg-slate-100 transition-colors">
                    <CheckCircle size={16} weight="fill" />
                  </button>
                )}
                <button onClick={() => handleMarkUnread(selected.id)} title="Mark as unread"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors">
                  <EnvelopeOpen size={16} />
                </button>
              </div>
            </div>

            <OtaRequestCard key={selected.id} thread={selected} onUpdated={(t) => { setSelected(t); reload(); }} />

            {/* Internal Notes Panel */}
            <AnimatePresence>
              {showNotes && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                  className="px-4 py-3 border-b border-amber-100 bg-amber-50/60 dark:bg-amber-900/10 space-y-2">
                  <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                    <Note size={11} weight="fill" /> Internal Notes — only visible to you
                  </p>
                  {(selected.notes || []).map(n => (
                    <div key={n.id} className="flex items-start gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 text-xs text-slate-700 dark:text-slate-200 shadow-sm">
                      <span className="flex-1">{n.text}</span>
                      <span className="text-slate-300 text-[9px] shrink-0">{n.created_at?.slice(0, 10)}</span>
                      <button onClick={() => handleDeleteNote(n.id)} className="text-slate-300 hover:text-red-400 shrink-0">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                  {(selected.notes || []).length === 0 && (
                    <p className="text-xs text-slate-400">No notes yet</p>
                  )}
                  <div className="flex gap-2">
                    <input value={noteText} onChange={e => setNoteText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleAddNote()}
                      placeholder="Add a note… (Enter to save)"
                      className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-amber-200 bg-white dark:bg-slate-700 dark:border-slate-600 text-slate-700 dark:text-slate-200 focus:outline-none focus:border-amber-400" />
                    <button onClick={handleAddNote}
                      className="px-3 py-1.5 bg-amber-400 text-white rounded-lg text-xs font-semibold hover:bg-amber-500 transition-colors">
                      Add
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Messages */}
            <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto p-4">
              <div className="space-y-3">
                {selected.messages?.map((m, i) => {
                  if (m.sender === "system") return (
                    <div key={m.id || i} className="flex justify-center">
                      <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-full">{m.text}</span>
                    </div>
                  );
                  return (
                    <div key={m.id || i} className={`flex ${m.sender === "owner" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                        m.sender === "owner"
                          ? "bg-amber-400 text-white"
                          : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                      }`}>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                        <p className={`text-[10px] mt-1 flex items-center gap-1 ${m.sender === "owner" ? "text-amber-100" : "text-slate-400"}`}>
                          {m.timestamp ? new Date(m.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}
                          {m.channel === "whatsapp" && <WhatsappLogo size={9} weight="fill" />}
                          {(m.wa_failed || m.ota_failed) && <span className="text-red-200 font-semibold">· not delivered</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick reply templates */}
            <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-700">
              <div className="flex gap-1.5 overflow-x-auto pb-1 items-center">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => setReplyText(t.text)}
                    className="shrink-0 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[10px] hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors font-medium">
                    {t.label}
                  </button>
                ))}
                <button onClick={openNewTmpl} title="Add template"
                  className="shrink-0 p-1 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors ml-1">
                  <PencilSimple size={13} />
                </button>
              </div>
            </div>

            {/* AI Smart Replies */}
            <AnimatePresence>
              {smartReplies && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                  className="px-4 py-3 border-t border-amber-100 bg-amber-50/60 dark:bg-amber-900/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-semibold text-amber-600 flex items-center gap-1">
                      <Sparkle size={11} weight="fill" /> AI Suggestions — click to use
                    </p>
                    <button onClick={() => setSmartReplies(null)} className="text-slate-400 hover:text-slate-600">
                      <X size={13} />
                    </button>
                  </div>
                  {smartReplies.length === 0 && (
                    <p className="text-xs text-slate-400">No suggestions — make sure the guest has sent a message first.</p>
                  )}
                  {smartReplies.map((s, i) => {
                    const isLocal = s.lang !== "en";
                    return (
                      <button key={i} onClick={() => { setReplyText(s.text); setSmartReplies(null); }}
                        className={`w-full text-left px-3 py-2 rounded-xl bg-white dark:bg-slate-800 border transition-colors text-xs text-slate-700 dark:text-slate-200 shadow-sm ${
                          isLocal ? "border-green-100 hover:border-green-300" : "border-amber-100 hover:border-amber-300"
                        }`}>
                        <span className={`text-[9px] font-bold uppercase tracking-wide block mb-0.5 ${isLocal ? "text-green-600" : "text-amber-500"}`}>
                          {isLocal ? detectedLang.name : s.tone}
                        </span>
                        {s.text}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Reply input */}
            <div className="p-3 border-t border-slate-100 dark:border-slate-700 space-y-2">
              {selected.guest_phone && (
                <button onClick={() => setSendViaWhatsApp(v => !v)}
                  className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg transition-colors ${
                    sendViaWhatsApp
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600"
                  }`}>
                  <WhatsappLogo size={13} weight="fill" className={sendViaWhatsApp ? "text-green-600" : "text-slate-400"} />
                  {sendViaWhatsApp ? "Sending via WhatsApp" : "Send via WhatsApp"}
                </button>
              )}
              <div className="flex gap-2">
                <button onClick={getSmartReplies} disabled={loadingAI} title="AI Smart Reply"
                  className="shrink-0 bg-amber-50 border border-amber-200 text-amber-500 hover:bg-amber-100 rounded-xl px-2.5 py-2 transition-colors disabled:opacity-50">
                  {loadingAI
                    ? <div className="w-4 h-4 border-2 border-amber-300 border-t-amber-500 rounded-full animate-spin" />
                    : <Sparkle size={16} weight="fill" />}
                </button>
                <Input
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSendReply()}
                  placeholder="Type a message…"
                  className="bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 flex-1"
                />
                <button onClick={handleSendReply}
                  className={`rounded-xl px-3 py-2 transition-colors ${
                    sendViaWhatsApp && selected.guest_phone
                      ? "bg-green-500 hover:bg-green-600 text-white"
                      : "bg-amber-400 hover:bg-amber-500 text-white"
                  }`}>
                  {sendViaWhatsApp && selected.guest_phone
                    ? <WhatsappLogo size={18} weight="fill" />
                    : <PaperPlaneTilt size={18} weight="fill" />}
                </button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="hidden md:flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
            <div className="text-center">
              <ChatCircle size={48} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>

      {/* Template Edit Dialog */}
      <Dialog open={tmplDialogOpen} onOpenChange={setTmplDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">
              {editTmpl ? "Edit Template" : "New Template"}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Use {"{guest_name}"}, {"{property_name}"}, {"{check_in}"}, {"{review_link}"} as placeholders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Label</Label>
              <Input value={tmplForm.label} onChange={e => setTmplForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Early Check-in"
                className="bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1" />
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Message Text</Label>
              <textarea value={tmplForm.text} onChange={e => setTmplForm(f => ({ ...f, text: e.target.value }))}
                rows={4} placeholder="Type your template message…"
                className="mt-1 w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 resize-none" />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            {editTmpl && (
              <button onClick={() => { handleDeleteTmpl(editTmpl.id); setTmplDialogOpen(false); }}
                className="bg-red-50 text-red-500 hover:bg-red-100 font-medium rounded-xl px-4 py-2 text-sm transition-colors">
                Delete
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button onClick={() => setTmplDialogOpen(false)}
                className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 font-medium rounded-xl px-4 py-2 text-sm transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveTmpl}
                className="bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-4 py-2 text-sm transition-colors flex items-center gap-1.5">
                <FloppyDisk size={14} /> Save
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
